import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ScrollText, Users } from 'lucide-react';
import { Button, Card, EmptyState, FormField, Input, Skeleton, toast } from '@/ui';
import { isOrgAdmin } from '@/auth/types';
import { useSessionStore } from '@/auth/session.store';
import { refineLocation, type TrLocation } from '@/lib/locations';
import { LocationFields } from '@/features/locations/LocationFields';
import { useLocations } from '@/features/locations/useLocations';
import {
  ORGANIZATION_FIELDS,
  fetchOrganization,
  updateOrganization,
  type OrganizationFieldKey,
} from './api';
import { MemberTypeChip } from './MemberTypeChip';
import { organizationKeys } from './query-keys';
import { useZoneBase } from './zone-base';
import { iamErrorMessage } from './error-message';
import styles from './Organization.module.css';

/**
 * ISLETME PROFILI (`/buro/isletme` ve `/musteri/isletme` — ayni bilesen iki bolgede).
 *
 * Rotanin kendisi `adminOnly` (bkz. screens.ts) ama ekran YINE DE kendi
 * kontrolunu yapiyor: bilesen ileride baska bir yerden de cizilebilir ve
 * "yalniz rota koruyor" varsayimi sessizce yanlislanabilir. Yonetici olmayan
 * kullaniciya alanlar SALT OKUNUR gosteriliyor — butonu gizlemek "neden
 * degismiyor" sorusunu dogururdu (mobil OrganizationProfile ile ayni karar).
 *
 * Sunucu govdeden YALNIZ beyaz listedeki alanlari okur; form o listeden
 * uretiliyor (ORGANIZATION_FIELDS), elle yazilmiyor.
 */

const makeOrganizationSchema = (t: TFunction, locations: readonly TrLocation[] = []) =>
  z
    .object({
      organizationName: z.string().trim().min(1, { message: t('org.errors.nameRequired') }),
      // Bos birakilabilir; DOLUYSA bicimi dogrulanir. `.optional()` degil: alan
      // her zaman var, degeri bos dize olabilir.
      organizationEmail: z
        .string()
        .trim()
        .refine((value) => value === '' || z.email().safeParse(value).success, {
          message: t('validation.emailInvalid'),
        }),
      organizationPhone: z.string(),
      organizationAddress: z.string(),
      organizationCity: z.string(),
      organizationDistrict: z.string(),
      businessChamber: z.string(),
      businessNumber: z.string(),
    })
    // Il/ilce bos birakilabilir; DOLUYSA listeden (M4).
    .superRefine(
      refineLocation({
        locations,
        cityField: 'organizationCity',
        districtField: 'organizationDistrict',
        message: t('locations.pickFromList'),
      }),
    );

type OrganizationFormValues = Record<OrganizationFieldKey, string>;

export function OrganizationProfilePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const snapshot = useSessionStore((state) => state.snapshot);
  const admin = isOrgAdmin(snapshot);
  // Bu ekran iki bolgede paylasiliyor; ic baglantilar cagiranin bolgesine gore uretilir.
  const zoneBase = useZoneBase();

  const query = useQuery({
    queryKey: organizationKeys.me(),
    queryFn: fetchOrganization,
  });

  const locations = useLocations();
  const schema = useMemo(() => makeOrganizationSchema(t, locations), [t, locations]);
  const fields = query.data?.organization.fields;

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<OrganizationFormValues>({
    resolver: zodResolver(schema),
    // Sunucudan gelen degerler `values` ile beslenir; useEffect + reset yerine
    // bu, "form once bos aciliyor sonra doluyor" yarisini hic dogurmuyor.
    ...(fields ? { values: fields } : {}),
    defaultValues: emptyValues(),
  });

  const save = useMutation({
    mutationFn: updateOrganization,
    onSuccess: async () => {
      toast({ message: t('members.orgUpdated'), tone: 'success' });
      // Sunucu bu cagriyi denetim kaydina yaziyor; iki liste de bayatladi.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: organizationKeys.me() }),
        queryClient.invalidateQueries({ queryKey: organizationKeys.auditLogRoot() }),
      ]);
    },
    onError: (error: unknown) => {
      toast({ message: iamErrorMessage(error, t), tone: 'danger' });
    },
  });

  const onSubmit = handleSubmit((values) => {
    save.mutate(values);
  });

  if (query.isPending) {
    return (
      <Card padding="lg">
        <Skeleton shape="block" height="220px" />
      </Card>
    );
  }

  // Bir isletmeye bagli olmayan kullanici: sunucu 403 doner, burada sebebi
  // yazili duruyor.
  if (query.isError || !query.data) {
    return (
      <Card padding="lg">
        <EmptyState
          tone="error"
          icon={<Building2 size={28} aria-hidden="true" />}
          title={t('org.profile.notMember')}
          description={t('org.profile.notMemberDetail')}
          action={
            <Button
              tone="neutral"
              onClick={() => {
                void query.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          }
        />
      </Card>
    );
  }

  const { organization, memberType } = query.data;

  return (
    <div className={styles.page}>
      <Card padding="lg">
        <div className={styles.pageHead}>
          <div>
            <h1 className={styles.title}>{organization.fields.organizationName || '—'}</h1>
            <p className={styles.subtitle}>
              {organization.memberCount} {t('org.profile.activeMembers')}
            </p>
          </div>
          <MemberTypeChip memberType={memberType} />
        </div>
      </Card>

      {!admin ? <p className={styles.notice}>{t('org.profile.staffReadOnly')}</p> : null}

      <Card title={t('profile.businessInfo')} padding="lg">
        <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
          <div className={styles.formGrid}>
            {ORGANIZATION_FIELDS.map((field) => {
              // Il/ilce serbest metin degil secici (M4): il alaninin yerinde ikisi birden.
              if (field.key === 'organizationDistrict') return null;
              if (field.key === 'organizationCity') {
                return (
                  <LocationFields
                    key="location"
                    control={control}
                    register={register}
                    setValue={setValue}
                    cityName="organizationCity"
                    districtName="organizationDistrict"
                    cityLabel={t('org.profile.city')}
                    districtLabel={t('org.profile.district')}
                    disabled={!admin}
                  />
                );
              }
              return (
                <FormField
                  key={field.key}
                  label={t(field.labelKey)}
                  error={errors[field.key]}
                  required={field.key === 'organizationName'}
                >
                  <Input readOnly={!admin} {...register(field.key)} />
                </FormField>
              );
            })}
          </div>

          {admin ? (
            <div className={styles.formActions}>
              <Button type="submit" loading={save.isPending} disabled={!isDirty}>
                {save.isPending ? t('org.profile.saving') : t('org.profile.save')}
              </Button>
            </div>
          ) : null}
        </form>
      </Card>

      {admin ? (
        <Card padding="md">
          <div className={styles.formActions}>
            <Link to={`${zoneBase}/ekip`} className={styles.linkButton}>
              <Users size={16} aria-hidden="true" />
              {t('org.profile.manageTeam')}
            </Link>
            <Link to={`${zoneBase}/gecmis`} className={styles.linkButton}>
              <ScrollText size={16} aria-hidden="true" />
              {t('profile.auditLog')}
            </Link>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

const emptyValues = (): OrganizationFormValues => {
  const values = {} as OrganizationFormValues;
  for (const field of ORGANIZATION_FIELDS) values[field.key] = '';
  return values;
};
